import { addDays } from "date-fns/addDays";
import { ReactNode, createContext, useCallback, useContext, useReducer } from "react";
import i18n from "../../services/localization-service/i18n";
import { CustomDurationDialogValues } from "../custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import storageService from "../../services/storage/storage-service/StorageService";
import _ from "lodash";

export const OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY = "ObjectHistoryViewOptionsState";

type ObjectHistoryStateType = {
    // Options state
    filterText: string;
    showMemos: boolean;
    showSettingsChanges: boolean;
    showAlarms: boolean;
    showDiagnosticEvents: boolean;
    showDataQualityEvents: boolean;
    showFormEvents: boolean;
    showManualEvents: boolean;
    durationLabel: string;
    durationOptions: CustomDurationDialogValues;

    scrollTo: number | undefined;
    // Other state
    isOptionsDirty: boolean;

    downloadSettingsUid: string | undefined;
    downloadAlarmsUid: string | undefined;

    hasAlarmHistory: boolean;
    hasSettingsHistory: boolean;
};

const initialState: ObjectHistoryStateType = {
    filterText: "",
    showMemos: true,
    showSettingsChanges: true,
    showAlarms: true,
    showDataQualityEvents: true,
    showDiagnosticEvents: true,
    showFormEvents: true,
    showManualEvents: true,
    isOptionsDirty: false,
    durationLabel: i18n.t('customDuration.last100'),
    durationOptions: {
        option: DurationOptionEnum.Default,
        duration: {
            startDate: addDays(new Date(), -7),
            endDate: new Date()
        }
    },
    scrollTo: undefined,
    downloadSettingsUid: undefined,
    downloadAlarmsUid: undefined,
    hasAlarmHistory: false,
    hasSettingsHistory: false
};


function getInitialState() {

    const defaultState = _.cloneDeep(initialState);
    defaultState.durationOptions.duration.startDate = addDays(new Date(), -7);
    defaultState.durationOptions.duration.endDate = new Date();
        

    // if reading options state from local storage, dates will be strings, so they have to be 
    // fixed here
    const storedOptionState: ObjectHistoryStateType | undefined = storageService.local.get<ObjectHistoryStateType | undefined>(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, undefined);

    if (storedOptionState) {
        //durationOptions: CustomDurationDialogValues;
        if (storedOptionState.durationOptions.duration.startDate) {
            storedOptionState.durationOptions.duration.startDate = new Date(storedOptionState.durationOptions.duration.startDate.toString());
        }
        if (storedOptionState.durationOptions.duration.endDate) {
            storedOptionState.durationOptions.duration.endDate = new Date(storedOptionState.durationOptions.duration.endDate.toString());
        }
    }

    return storedOptionState ?? defaultState;
}



enum ObjectHistoryActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    ShowMemosChanged = "SHOW_MEMOS_CHANGED",
    ShowSettingsChangesChanged = "SHOW_SETTINGS_CHANGES_CHANGED",
    ShowAlarmsChanged = "SHOW_ALARMS_CHANGED",
    ShowDiagnosticEventsChanged = "SHOW_DIAGNOSTIC_EVENTS_CHANGED",
    ShowDataQualityEventsChanged = "SHOW_DATA_QUALITY_EVENTS_CHANGED",
    ShowFormEventsChanged = "SHOW_FORM_EVENTS_CHANGED",
    ShowManualEventsChanged = "SHOW_MANUAL_EVENTS_CHANGED",
    DurationChanged = "DURATION_CHANGED",
    ScrollToChanged = "HISTORY_SCROLLTO_CHANGED",
    DownloadSettingsUidChanged = 'DOWNLOAD_SETTINGS_UID_CHANGED',
    DownloadAlarmsUidChanged = 'DOWNLOAD_ALARMS_UID_CHANGED',
    HasAlarmHistoryChanged = 'HAS_ALARM_HISTORY_CHANGED',
    HasSettingsHistoryChanged = 'HAS_SETTINGS_HISTORY_CHANGED',
}

type ObjectHistoryActionTypes = {
    type: ObjectHistoryActionEnum.ResetOptionsRequested;
} | {
    type: ObjectHistoryActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: ObjectHistoryActionEnum.ShowMemosChanged;
    showMemos: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowSettingsChangesChanged;
    showSettingsChanges: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowAlarmsChanged;
    showAlarms: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowDiagnosticEventsChanged;
    showDiagnosticEvents: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowDataQualityEventsChanged;
    showDataQualityEvents: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowFormEventsChanged;
    showFormEvents: boolean;
} | {
    type: ObjectHistoryActionEnum.ShowManualEventsChanged;
    showManualEvents: boolean;
} | {
    type: ObjectHistoryActionEnum.ScrollToChanged;
    payload: number | undefined;
} | {
    type: ObjectHistoryActionEnum.DurationChanged;
    duration: CustomDurationDialogValues;
} |  {
    type: ObjectHistoryActionEnum.DownloadSettingsUidChanged;
    payload: string | undefined;
} |  {
    type: ObjectHistoryActionEnum.DownloadAlarmsUidChanged;
    payload: string | undefined;
} |  {
    type: ObjectHistoryActionEnum.HasAlarmHistoryChanged;
    payload: boolean;
} |  {
    type: ObjectHistoryActionEnum.HasSettingsHistoryChanged;
    payload: boolean;
};

function reducer(state: ObjectHistoryStateType, action: ObjectHistoryActionTypes): ObjectHistoryStateType {

    const copy = { ...state };
    switch (action.type) {

        case ObjectHistoryActionEnum.ResetOptionsRequested:

            storageService.local.remove(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY);

            return getInitialState();

            // copy.filterText = initialState.filterText;
            // copy.showMemos = initialState.showMemos;
            // copy.showSettingsChanges = initialState.showSettingsChanges;
            // copy.showAlarms = initialState.showAlarms;
            // copy.showDiagnosticEvents = initialState.showDiagnosticEvents;
            // copy.showDataQualityEvents = initialState.showDataQualityEvents;
            // copy.showManualEvents = initialState.showManualEvents;
            // copy.showFormEvents = initialState.showFormEvents;
            // copy.isOptionsDirty = false;
            // copy.durationOptions = {
            //     option: initialState.durationOptions.option,
            //     duration: {
            //         startDate: new Date(initialState.durationOptions.duration.startDate as Date),
            //         endDate: new Date(initialState.durationOptions.duration.endDate as Date)
            //     }
            // };
            // copy.durationLabel = initialState.durationLabel;
            //return copy;

        case ObjectHistoryActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case ObjectHistoryActionEnum.ShowMemosChanged:
            copy.showMemos = action.showMemos;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowSettingsChangesChanged:
            copy.showSettingsChanges = action.showSettingsChanges;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowAlarmsChanged:
            copy.showAlarms = action.showAlarms;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowDiagnosticEventsChanged:
            copy.showDiagnosticEvents = action.showDiagnosticEvents;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowDataQualityEventsChanged:
            copy.showDataQualityEvents = action.showDataQualityEvents;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowFormEventsChanged:
            copy.showFormEvents = action.showFormEvents;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ShowManualEventsChanged:
            copy.showManualEvents = action.showManualEvents;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.DurationChanged:
            copy.durationLabel = action.duration.option === DurationOptionEnum.Default ?
                i18n.t('customDuration.last100') :
                CustomDurationUtils.getCustomDurationLabel(action.duration.option, true);

            copy.durationOptions = action.duration;
            copy.isOptionsDirty = isOptionsDirty(copy);
            storageService.local.set(OBJECT_HISTORY_VIEW_OPTION_STORAGE_KEY, copy);
            return copy;

        case ObjectHistoryActionEnum.ScrollToChanged:

            copy.scrollTo = action.payload;
            return copy;

        case ObjectHistoryActionEnum.DownloadSettingsUidChanged:
            copy.downloadSettingsUid = action.payload;
            return copy;

        case ObjectHistoryActionEnum.DownloadAlarmsUidChanged:
            copy.downloadAlarmsUid = action.payload;
            return copy;
        case ObjectHistoryActionEnum.HasAlarmHistoryChanged:
            copy.hasAlarmHistory = action.payload;
            return copy;

        case ObjectHistoryActionEnum.HasSettingsHistoryChanged:
            copy.hasSettingsHistory = action.payload;
            return copy;
    }
}

function isOptionsDirty(state: ObjectHistoryStateType) {
    return state.filterText !== initialState.filterText ||
        state.showMemos !== initialState.showMemos ||
        state.showSettingsChanges !== initialState.showSettingsChanges ||
        state.showAlarms !== initialState.showAlarms ||
        state.showDiagnosticEvents !== initialState.showDiagnosticEvents ||
        state.showDataQualityEvents !== initialState.showDataQualityEvents ||
        state.showFormEvents !== initialState.showFormEvents ||
        state.showManualEvents !== initialState.showManualEvents ||
        state.durationOptions.option !== initialState.durationOptions.option;
}

type UseObjectHistoryStateType = {
    state: ObjectHistoryStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setShowMemos: (showMemos: boolean) => void;
    setShowSettingsChanges: (showSettingsChanges: boolean) => void;
    setShowAlarms: (showAlarms: boolean) => void;
    setShowDiagnosticEvents: (showDiagnosticEvents: boolean) => void;
    setShowDataQualityEvents: (showDataQualityEvents: boolean) => void;
    setShowFormEvents: (showFormEvents: boolean) => void;
    setShowManualEvents: (showManualEvents: boolean) => void;
    setDuration: (duration: CustomDurationDialogValues) => void;
    setScrollTo: (payload: number | undefined) => void;
    setDownloadSettingsUid: (payload: string | undefined) => void;
    setDownloadAlarmsUid: (payload: string | undefined) => void;
    setHasAlarmHistory: (payload: boolean) => void;
    setHasSettingsHistory: (payload: boolean) => void;
};

function useObjectHistoryState(): UseObjectHistoryStateType {
    const [state, dispatch] = useReducer(reducer, getInitialState());

    const resetOptions = useCallback(() => dispatch({
        type: ObjectHistoryActionEnum.ResetOptionsRequested
    }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
        type: ObjectHistoryActionEnum.FilterTextChanged,
        filterText: filterText
    }), []);


    const setShowMemos = useCallback((showMemos: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowMemosChanged,
        showMemos: showMemos
    }), []);

    const setShowSettingsChanges = useCallback((showSettingsChanges: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowSettingsChangesChanged,
        showSettingsChanges: showSettingsChanges
    }), []);

    const setShowAlarms = useCallback((showAlarms: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowAlarmsChanged,
        showAlarms: showAlarms
    }), []);

    const setShowDiagnosticEvents = useCallback((showDiagnosticEvents: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowDiagnosticEventsChanged,
        showDiagnosticEvents: showDiagnosticEvents
    }), []);

    const setShowDataQualityEvents = useCallback((showDataQualityEvents: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowDataQualityEventsChanged,
        showDataQualityEvents: showDataQualityEvents
    }), []);

    const setShowFormEvents = useCallback((showFormEvents: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowFormEventsChanged,
        showFormEvents: showFormEvents
    }), []);

    const setShowManualEvents = useCallback((showManualEvents: boolean) => dispatch({
        type: ObjectHistoryActionEnum.ShowManualEventsChanged,
        showManualEvents: showManualEvents
    }), []);

    const setDuration = useCallback((duration: CustomDurationDialogValues) => dispatch({
        type: ObjectHistoryActionEnum.DurationChanged,
        duration: duration
    }), []);

    const setScrollTo = useCallback((value: number | undefined) => dispatch({
        type: ObjectHistoryActionEnum.ScrollToChanged,
        payload: value
    }), []);

    const setDownloadSettingsUid = useCallback((value: string | undefined) => dispatch({
        type: ObjectHistoryActionEnum.DownloadSettingsUidChanged,
        payload: value
    }), []);

    const setDownloadAlarmsUid = useCallback((value: string | undefined) => dispatch({
        type: ObjectHistoryActionEnum.DownloadAlarmsUidChanged,
        payload: value
    }), []);

    const setHasAlarmHistory = useCallback((value: boolean) => dispatch({
        type: ObjectHistoryActionEnum.HasAlarmHistoryChanged,
        payload: value
    }), []);
    const setHasSettingsHistory = useCallback((value: boolean) => dispatch({
        type: ObjectHistoryActionEnum.HasSettingsHistoryChanged,
        payload: value
    }), []);

    return {
        state,
        resetOptions,
        setFilterText,
        setShowMemos,
        setShowSettingsChanges,
        setShowAlarms,
        setShowDiagnosticEvents,
        setShowDataQualityEvents,
        setShowFormEvents,
        setShowManualEvents,
        setDuration,
        setScrollTo,
        setDownloadSettingsUid,
        setDownloadAlarmsUid,
        setHasAlarmHistory,
        setHasSettingsHistory
    };
}

const ObjectHistoryContext = createContext<UseObjectHistoryStateType | undefined>(undefined);

export type ObjectHistoryContextProviderProps = {
    children: ReactNode;
};

export default function ObjectHistoryContextProvider({ children }: ObjectHistoryContextProviderProps) {
    const objectHistoryState = useObjectHistoryState();

    return (
        <ObjectHistoryContext.Provider value={objectHistoryState}>
            {children}
        </ObjectHistoryContext.Provider>
    );
}

export function useObjectHistoryContext() {
    const context = useContext<UseObjectHistoryStateType | undefined>(ObjectHistoryContext);
    if (!context) {
        throw new Error("useObjectHistoryContext must be used within a ObjectHistoryContextProvider");
    }
    return context;
}